import { FormattedMessage, useIntl } from 'react-intl';
import { DateTime } from 'luxon';
import type { IssuedTicketBobcatDataDTO } from '@/api/trafikportalen';
import { FormElement } from '@/components/FormElement';
import {
  formatDateTimeReadable,
  formatRelativeDateTime,
  formatTimeLeft,
} from '@/helpers/formatting';
import { ongoingActivationEnd, showsActivationsLeft } from '@/helpers/ticket';

type TicketActivationDetailsData = Pick<
  IssuedTicketBobcatDataDTO,
  'activations_left' | 'activations_total' | 'current_validity_end'
>;

interface TicketActivationDetailsProps {
  bobcatData?: TicketActivationDetailsData;
}

export const TicketActivationDetails = ({
  bobcatData,
}: TicketActivationDetailsProps) => {
  const { formatMessage } = useIntl();

  if (!bobcatData || !showsActivationsLeft(bobcatData)) return null;

  const activationEnd = ongoingActivationEnd(bobcatData);

  return (
    <>
      <FormElement
        label={formatMessage({ defaultMessage: 'Aktiveringar kvar:' })}
        input={
          <p className="break-words">
            <FormattedMessage
              defaultMessage="{left} av {total}"
              values={{
                left: bobcatData.activations_left,
                total: bobcatData.activations_total,
              }}
            />
          </p>
        }
      />
      {activationEnd ? (
        <FormElement
          label={formatMessage({ defaultMessage: 'Pågående aktivering:' })}
          input={
            <p className="break-words">
              <span aria-hidden="true">
                <FormattedMessage
                  defaultMessage="Aktiv till {end} ({timeLeft})"
                  values={{
                    end: formatRelativeDateTime(activationEnd),
                    timeLeft: formatTimeLeft(
                      DateTime.fromISO(activationEnd),
                      formatMessage,
                    ),
                  }}
                />
              </span>
              <span className="sr-only">
                <FormattedMessage
                  defaultMessage="Aktiv till {end}"
                  values={{ end: formatDateTimeReadable(activationEnd) }}
                />
              </span>
            </p>
          }
        />
      ) : null}
    </>
  );
};
